import { MUTE_KEY } from './constants';

type SoundId =
  | 'paddle'
  | 'brick'
  | 'destroy'
  | 'damage'
  | 'pickup'
  | 'activate'
  | 'life'
  | 'explosion'
  | 'complete'
  | 'gameover'
  | 'victory';

class AudioSys {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  enabled = loadMute() === false;

  setEnabled(v: boolean): void {
    this.enabled = v;
    try {
      window.localStorage.setItem(MUTE_KEY, v ? '0' : '1');
    } catch {
      /* ignore */
    }
  }

  toggle(): boolean {
    this.setEnabled(!this.enabled);
    return this.enabled;
  }

  private ac(): AudioContext {
    if (!this.ctx) {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.28;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  play(id: SoundId): void {
    if (!this.enabled) return;
    try {
      switch (id) {
        case 'paddle':
          this.blip(220, 0.06, 'square', 0.12);
          break;
        case 'brick':
          this.blip(520, 0.05, 'triangle', 0.1);
          break;
        case 'damage':
          this.blip(340, 0.07, 'sawtooth', 0.1);
          break;
        case 'destroy':
          this.sweep(740, 420, 0.12, 'sine', 0.14);
          break;
        case 'pickup':
          this.arpeggio([660, 880, 1180], 0.07);
          break;
        case 'activate':
          this.arpeggio([392, 523, 659], 0.08);
          break;
        case 'life':
          this.arpeggio([523, 659, 784, 1046], 0.09);
          break;
        case 'explosion':
          this.noise(0.22, 280);
          break;
        case 'complete':
          this.arpeggio([523, 659, 784, 1046], 0.11);
          break;
        case 'gameover':
          this.sweep(392, 180, 0.45, 'triangle', 0.2);
          break;
        case 'victory':
          this.arpeggio([523, 659, 784, 988, 1174], 0.1);
          break;
      }
    } catch {
      /* audio is optional */
    }
  }

  private blip(freq: number, dur: number, type: OscillatorType, vol: number): void {
    const ctx = this.ac();
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    g.gain.setValueAtTime(vol, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
    osc.connect(g);
    g.connect(this.master!);
    osc.start();
    osc.stop(ctx.currentTime + dur);
  }

  private sweep(from: number, to: number, dur: number, type: OscillatorType, vol: number): void {
    const ctx = this.ac();
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(from, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(Math.max(40, to), ctx.currentTime + dur);
    g.gain.setValueAtTime(vol, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
    osc.connect(g);
    g.connect(this.master!);
    osc.start();
    osc.stop(ctx.currentTime + dur);
  }

  private arpeggio(notes: number[], step: number): void {
    notes.forEach((f, i) => {
      const ctx = this.ac();
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      const t = ctx.currentTime + i * step;
      osc.type = 'triangle';
      osc.frequency.value = f;
      g.gain.setValueAtTime(0.16, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
      osc.connect(g);
      g.connect(this.master!);
      osc.start(t);
      osc.stop(t + 0.22);
    });
  }

  private noise(dur: number, cutoff: number): void {
    const ctx = this.ac();
    const len = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = cutoff;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.55, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
    src.connect(f);
    f.connect(g);
    g.connect(this.master!);
    src.start();
  }
}

function loadMute(): boolean {
  try {
    return window.localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export const audio = new AudioSys();
